import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Building2, 
  Layers, 
  DollarSign, 
  TrendingUp,
  Users,
  ArrowRight,
  Plus,
  Settings,
  Eye
} from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const PLAN_COLORS = {
  basico: 'bg-slate-100 text-slate-700',
  pro: 'bg-blue-100 text-blue-700',
  business: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700'
};

const PLAN_NAMES = {
  basico: 'Básico',
  pro: 'Pro',
  business: 'Business',
  enterprise: 'Enterprise'
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [resumen, setResumen] = useState(null);
  const [empresas, setEmpresas] = useState([]);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [resumenRes, empresasRes] = await Promise.all([
        api.get('/admin/resumen'),
        api.get('/empresas')
      ]);
      setResumen(resumenRes.data);
      setEmpresas(empresasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <div className="h-8 w-48 bg-slate-200 rounded animate-pulse"></div>
        <div className="grid grid-cols-2 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-24 bg-slate-200 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-mobile">
          <div>
            <h1 style={{ fontFamily: 'Outfit, sans-serif' }}>
              Panel de Control
            </h1>
            <p className="text-xs text-muted-foreground">
              Administración de FinScope
            </p>
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* KPIs */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Empresas</p>
                  <p className="text-xl font-bold">{resumen?.total_empresas || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-xs text-muted-foreground">Marcas</p>
                  <p className="text-xl font-bold">{resumen?.total_marcas || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-xs text-muted-foreground">Ingreso Mensual</p>
                  <p className="text-lg font-bold text-green-700">
                    {formatCurrency(resumen?.ingreso_mensual_total)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="text-xs text-muted-foreground">Anual Proyectado</p>
                  <p className="text-lg font-bold text-amber-700">
                    {formatCurrency(resumen?.ingreso_anual_proyectado)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Distribución por Plan */}
        <Card className="mb-6">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-semibold">Distribución por Plan</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(resumen?.por_plan || {}).map(([plan, count]) => (
                <div key={plan} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Badge className={PLAN_COLORS[plan]}>{PLAN_NAMES[plan]}</Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">{count}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(resumen?.ingreso_por_plan?.[plan])}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Acciones Rápidas */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Button 
            onClick={() => navigate('/admin/empresas')} 
            className="h-auto py-4 flex-col gap-2"
            data-testid="btn-empresas"
          >
            <Building2 className="w-5 h-5" />
            <span className="text-xs">Empresas</span>
          </Button>
          <Button 
            onClick={() => navigate('/admin/marcas')} 
            variant="outline"
            className="h-auto py-4 flex-col gap-2"
            data-testid="btn-marcas"
          >
            <Layers className="w-5 h-5" />
            <span className="text-xs">Marcas</span>
          </Button>
          <Button 
            onClick={() => navigate('/admin/usuarios')} 
            variant="outline"
            className="h-auto py-4 flex-col gap-2"
            data-testid="btn-usuarios"
          >
            <Users className="w-5 h-5" />
            <span className="text-xs">Usuarios</span>
          </Button>
          <Button 
            onClick={() => navigate('/admin/planes')} 
            variant="outline"
            className="h-auto py-4 flex-col gap-2"
            data-testid="btn-planes"
          >
            <Settings className="w-5 h-5" />
            <span className="text-xs">Planes</span>
          </Button>
        </div>

        {/* Lista de Clientes */}
        <Card>
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold">Clientes de FinScope</CardTitle>
            <Button size="sm" onClick={() => navigate('/admin/empresas')}>
              <Plus className="w-4 h-4 mr-1" />
              Nueva
            </Button>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="space-y-3">
              {resumen?.top_empresas?.map((empresa) => (
                <div 
                  key={empresa.id} 
                  className="flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/admin/empresas/${empresa.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${empresa.activo ? 'bg-primary/10' : 'bg-slate-100'}`}>
                      <Building2 className={`w-5 h-5 ${empresa.activo ? 'text-primary' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{empresa.nombre}</p>
                      <p className="text-xs text-muted-foreground">{empresa.num_marcas} marcas</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-sm text-green-600">
                      {formatCurrency(empresa.ingreso_mensual)}
                    </p>
                    <p className="text-xs text-muted-foreground">/mes</p>
                  </div>
                </div>
              ))}
              
              {(!resumen?.top_empresas || resumen.top_empresas.length === 0) && (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No hay empresas registradas</p>
                  <Button 
                    className="mt-4" 
                    onClick={() => navigate('/admin/empresas')}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Primera Empresa
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
